import { Components } from '@mui/material';

import { Theme } from '../../types';

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: ({ palette }) => ({
      'html *::-webkit-scrollbar-thumb': {
        borderRadius: '8px !important',
        backgroundColor: `${palette.text3}80 !important`,
      },
      'html *::-webkit-scrollbar': {
        width: '4px !important',
      },
      'html *::-webkit-scrollbar-track': {
        borderRadius: '4px !important',
        backgroundColor: 'transparent !important',
        border: 'none !important',
      },
      'html *': {
        scrollbarColor: 'inherit !important',
        scrollbarWidth: 'inherit !important',
        scrollBehavior: 'inherit !important',
      },
    }),
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root({ theme }) {
        return {
          backgroundColor: theme.palette.surface2,
          borderColor: theme.palette.inputNormal,
          borderRadius: `${theme.shape.borderRadius * 2}px`,

          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },

          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.inputDisabled,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.inputActive,
          },
        };
      },
    },
  },

  MuiList: {
    styleOverrides: {
      root() {
        return {
          padding: 0,
        };
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper({ theme }) {
        return {
          backgroundColor: theme.palette.dropdownMenu,
        };
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      root({ theme }) {
        return {
          '& .MuiOutlinedInput-root': {
            padding: theme.spacing(0, 2, 0, 3),
          },

          '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
            padding: 0,
          },

          '& .MuiAutocomplete-endAdornment': {
            position: 'initial',
          },

          '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root':
            {
              paddingRight: 0,
            },
        };
      },
      paper({ theme }) {
        return {
          backgroundColor: theme.palette.dropdownMenu,
        };
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root({ theme }) {
        return {
          padding: theme.spacing(2, 3),
          ...theme.typography.body6,

          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.surface3
                : theme.palette.surface2,
          },
        };
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      select() {
        return {
          padding: 0,
          display: 'flex',
        };
      },
      icon({ theme }) {
        return {
          color: theme.palette.text1,
        };
      },
    },
  },

  MuiToggleButton: {
    styleOverrides: {
      root({ theme }) {
        return {
          padding: theme.spacing(2, 4),
          backgroundColor: theme.palette.surface2,
          color: theme.palette.text3,

          '&:hover': {
            backgroundColor: theme.palette.surface3,
          },

          '&.MuiToggleButtonGroup-grouped': {
            '&:not(:first-of-type)': {
              borderLeft: `1px solid ${theme.palette.divider}`,
            },
          },

          '&.Mui-selected': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.surface3,

            '&:hover': {
              backgroundColor: theme.palette.surface3,
            },
          },
        };
      },
      sizeSmall() {
        return {
          height: '36px',
        };
      },
      sizeMedium() {
        return {
          height: '44px',
        };
      },
    },
  },

  MuiButtonBase: {
    styleOverrides: {
      root() {
        return {
          '&:focus, &:active': {
            outline: 'none',
          },
        };
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        minWidth: 'auto',
      },
      contained({ theme }) {
        return {
          backgroundColor: theme.palette.stateNormal,
          color: theme.palette.textWhite,
          border: 'none',

          '&:hover': {
            backgroundColor: theme.palette.stateHover,
            border: 'none',
          },

          '&:disabled': {
            color: theme.palette.textWhite,
            backgroundColor: theme.palette.stateDisabled,
            border: 'none',
          },
        };
      },
      outlined({ theme }) {
        return {
          backgroundColor: 'transparent',
          color: theme.palette.stateNormal,
          border: `1px solid ${theme.palette.stateNormal}`,

          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.stateHover,
            border: `1px solid ${theme.palette.stateHover}`,
          },

          '&:disabled': {
            color: theme.palette.textWhite,
            backgroundColor: theme.palette.stateDisabled,
            border: 'none',
          },
        };
      },
      text({ theme }) {
        return {
          color: theme.palette.stateNormal,

          '&:hover': {
            color: theme.palette.stateHover,
          },

          '&:disabled': {
            color: theme.palette.stateDisabled,
          },
        };
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root() {
        return {
          backgroundImage: 'none',
        };
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root({ theme }) {
        return {
          backgroundColor: theme.palette.surface2,
        };
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper({ theme }) {
        return {
          backgroundColor: theme.palette.surface2,
        };
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root({ theme }) {
        return {
          borderColor: theme.palette.divider,
        };
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root({ theme }) {
        return {
          color: theme.palette.stateNormal,

          '&:disabled': {
            color: theme.palette.stateDisabled,
          },
        };
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root({ theme }) {
        return { padding: theme.spacing(4), minWidth: '320px' };
      },
      action: { paddingTop: 0 },
      icon: { padding: 0 },
      message: { padding: 0 },
      standardError({ theme }) {
        return {
          backgroundColor: theme.palette.alertError,
        };
      },
      standardInfo({ theme }) {
        return {
          backgroundColor: theme.palette.alertInfo,
        };
      },
      standardSuccess({ theme }) {
        return {
          backgroundColor: theme.palette.alertSuccess,
        };
      },
      standardWarning({ theme }) {
        return {
          backgroundColor: theme.palette.alertWarning,
        };
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip({ theme }) {
        return {
          boxShadow: theme.shadows[1],
          backgroundColor: theme.palette.tooltipFill,
          color: theme.palette.textWhite,
          padding: theme.spacing(1, 2),
          ...theme.typography.body8,
        };
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper({ theme }) {
        return {
          width: '480px',

          [theme.breakpoints.down('sm')]: {
            margin: 0,
            width: '100%',
          },
        };
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: 0,
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root({ theme }) {
        return {
          height: 'min-content',
          color: theme.palette.text3,
        };
      },
    },
  },

  MuiTypography: {
    defaultProps: {
      variantMapping: {
        display1: 'h1',
        display2: 'h1',
        display3: 'h1',

        headline1: 'h2',
        headline2: 'h2',
        headline3: 'h2',

        title1: 'h3',
        title2: 'h3',
        title3: 'h3',
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root({ theme }) {
        return {
          height: 'auto',
          padding: theme.spacing(1, 2),
          borderRadius: `${theme.shape.borderRadius * 50}px`,
        };
      },
      label: {
        padding: 0,
      },
      filled({ theme }) {
        return {
          backgroundColor: theme.palette.chip,
          border: '1px solid transparent',
        };
      },
      outlined({ theme }) {
        return {
          border: `1px solid ${theme.palette.divider}`,
        };
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root({ theme }) {
        return {
          textTransform: 'none',
          minHeight: '48px',
          color: theme.palette.text2,
          '&:hover': {
            background:
              'linear-gradient(180deg, rgba(49, 121, 255, 0.00) 0%, rgba(49, 121, 255, 0.10) 100%)',
          },
          '&.Mui-selected': {
            background:
              'linear-gradient(180deg, rgba(49, 121, 255, 0.00) 0%, rgba(49, 121, 255, 0.10) 100%)',
          },
        };
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root() {
        return {
          background: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
        };
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      content({ theme }) {
        return {
          margin: theme.spacing(6, 0),
        };
      },
      expandIconWrapper({ theme }) {
        return {
          backgroundColor: theme.palette.grey?.[900],
          borderRadius: `${theme.shape.borderRadius * 4}px`,
          border: `1px solid ${theme.palette.grey?.[700]}`,
          width: theme.spacing(8),
          minWidth: theme.spacing(8),
          height: theme.spacing(8),
          marginLeft: theme.spacing(2),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        };
      },
    },
  },
};
